'use client';

import Link from 'next/link';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FileText, Search, Filter, Calendar as CalendarIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import { DatePicker } from '@/components/ui/date-picker';

const reports = [
  {
    id: '257',
    student: 'Pedro Wilson Arantes Moura',
    instructor: 'João Silva',
    date: '24/07/2025',
    status: 'Com Alerta',
  },
  {
    id: '256',
    student: 'Ana Clara Souza',
    instructor: 'Maria Oliveira',
    date: '23/07/2025',
    status: 'Validada',
  },
  {
    id: '255',
    student: 'Lucas Martins',
    instructor: 'João Silva',
    date: '22/07/2025',
    status: 'Invalidada',
  },
  {
    id: '254',
    student: 'Mariana Costa',
    instructor: 'Carlos Pereira',
    date: '21/07/2025',
    status: 'Validada',
  },
  {
    id: '253',
    student: 'Fernando Almeida',
    instructor: 'Maria Oliveira',
    date: '20/07/2025',
    status: 'Validada',
  },
];

type ReportStatus = 'Validada' | 'Invalidada' | 'Com Alerta';

const statusStyles: Record<ReportStatus, string> = {
  Validada: 'bg-green-100 text-green-800 border-green-200',
  Invalidada: 'bg-red-100 text-red-800 border-red-200',
  'Com Alerta': 'bg-yellow-100 text-yellow-800 border-yellow-200',
};

export default function ReportsPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 md:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
             <Image 
                src="/images/logo.svg" 
                alt="Logo" 
                width={150} 
                height={35} 
                data-ai-hint="logo"
              />
          </div>
          <h1 className="text-xl font-semibold text-gray-800">
            Painel de Relatórios de Aulas
          </h1>
          <div>
            {/* Placeholder for user menu or other actions */}
          </div>
        </div>
      </header>
      <main className="container mx-auto p-4 md:p-8">
        <Card>
          <CardHeader>
            <CardTitle>Histórico de Aulas</CardTitle>
            <CardDescription>
              Visualize e gerencie os relatórios de todas as aulas práticas.
            </CardDescription>
             <div className="flex flex-wrap items-center gap-2 pt-4">
                <div className="relative flex-grow sm:flex-grow-0">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input placeholder="Buscar por aluno ou instrutor..." className="pl-10 w-full sm:w-64" />
                </div>
                 <div className="relative">
                  <Input type="number" placeholder="Nº da Aula" className="w-full sm:w-32" />
                </div>
                 <DatePicker />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="ml-auto">
                      <Filter className="mr-2 h-4 w-4" />
                      Filtrar Status
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Filtrar por Status</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuCheckboxItem checked>
                      Todos
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem>Validada</DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem>Invalidada</DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem>Com Alerta</DropdownMenuCheckboxItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Aula Nº</TableHead>
                  <TableHead>Aluno</TableHead>
                  <TableHead>Instrutor</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell className="font-medium">{report.id}</TableCell>
                    <TableCell>{report.student}</TableCell>
                    <TableCell>{report.instructor}</TableCell>
                    <TableCell>{report.date}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn(statusStyles[report.status as ReportStatus])}
                      >
                        {report.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                       <Link href={'/report/' + report.id} passHref>
                          <Button variant="outline" size="sm">
                            <FileText className="mr-2 h-4 w-4" />
                            Ver Relatório
                          </Button>
                        </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
